import { SkillName, categories } from '../components/UserAndProfile/UserContext';

export type StrategyCardData = {
  skill: string;
  headline: string;
  steps: string[];
  whenToUse: string;
  commonTrap?: string;
};

export const STRATEGY_CARD_PROMPTS = [
  "What's the key knowledge / strategy I should know?",
  "What's a test-taking strategy I could use here?",
  "What strategy could I have used?",
] as const;

export function isStrategyCardPrompt(prompt: string): boolean {
  const normalized = prompt.trim().toLowerCase();
  return STRATEGY_CARD_PROMPTS.some((p) => p.toLowerCase() === normalized);
}

const cards: Record<string, StrategyCardData> = {
  // --- Top-frequency R&W ---
  'Word Choice': {
    skill: 'Word Choice',
    headline: 'Pick the word that fits meaning + tone',
    steps: [
      'Cover the blank and predict the meaning in your own words.',
      'Eliminate choices that change the meaning or feel too extreme/casual for the passage.',
      'Plug the remaining word back in and check that the sentence still flows.',
    ],
    whenToUse: 'Any fill-in-the-blank vocabulary or precision-of-language question.',
    commonTrap: 'A synonym that is close in meaning but wrong in tone or context.',
  },
  'Punctuation': {
    skill: 'Punctuation',
    headline: 'Decide if you need a stop, a pause, or nothing',
    steps: [
      'Read the sentence without punctuation and find the two clauses.',
      'Ask: Are both sides complete sentences? (period/semicolon) Or is one incomplete? (comma/colon/dash).',
      'Eliminate choices that create a run-on or a fragment.',
    ],
    whenToUse: 'Comma, semicolon, colon, dash, or apostrophe questions.',
    commonTrap: 'Using a comma alone to join two complete sentences (comma splice).',
  },
  'Transition Words': {
    skill: 'Transition Words',
    headline: 'Match the logical relationship',
    steps: [
      'Read the sentence before and after the blank.',
      'Decide the relationship: contrast, cause/effect, example, or continuation.',
      'Pick the transition that matches that relationship — ignore fancy wording.',
    ],
    whenToUse: 'Questions asking which word or phrase best connects ideas.',
    commonTrap: 'A transition that sounds formal but signals the wrong relationship.',
  },
  'Main Idea': {
    skill: 'Main Idea',
    headline: 'Find what the whole passage is about',
    steps: [
      'Skim the first and last sentences of the passage or paragraph.',
      'Ask: What is the author\'s main point in one sentence?',
      'Eliminate choices that are too narrow (one detail) or too broad (beyond the text).',
    ],
    whenToUse: 'Questions about central idea, purpose, or what the passage is mainly about.',
    commonTrap: 'A true detail that is not the main idea.',
  },
  'Inferences': {
    skill: 'Inferences',
    headline: 'Prove it with the text — don\'t invent',
    steps: [
      'Underline the claim the question asks you to support.',
      'Find the sentence(s) that most directly back it up.',
      'Choose the answer that must be true based on the text, not outside knowledge.',
    ],
    whenToUse: 'Questions that ask what can be reasonably inferred or concluded.',
    commonTrap: 'An answer that is plausible in real life but not supported by the passage.',
  },
  'Supporting Claims': {
    skill: 'Supporting Claims',
    headline: 'Evidence must directly back the claim',
    steps: [
      'Identify the claim in the question or underlined sentence.',
      'For each choice, ask: Does this specifically support that claim?',
      'Eliminate evidence that is related but doesn\'t prove the claim.',
    ],
    whenToUse: 'Which choice best supports / provides the best evidence questions.',
    commonTrap: 'Evidence that supports a different claim nearby in the passage.',
  },
  'Function of Sentence': {
    skill: 'Function of Sentence',
    headline: 'Ask what the sentence does, not what it says',
    steps: [
      'Read the sentence and the sentences around it.',
      'Label its job: introduce, contrast, exemplify, conclude, or shift focus.',
      'Match that job to the answer choice wording.',
    ],
    whenToUse: 'Questions about the purpose or function of a sentence/paragraph.',
    commonTrap: 'Restating the content instead of describing the rhetorical role.',
  },
  'Synthesizing Notes': {
    skill: 'Synthesizing Notes',
    headline: 'Combine notes without adding new claims',
    steps: [
      'Read all the student notes carefully.',
      'Find what they share or how they relate (agree, contrast, build on each other).',
      'Pick the choice that accurately blends them — no extra facts.',
    ],
    whenToUse: 'Questions with bullet notes to combine into one sentence or claim.',
    commonTrap: 'A polished sentence that invents a connection the notes never make.',
  },
  'Two Passages': {
    skill: 'Two Passages',
    headline: 'Compare stance, then evidence',
    steps: [
      'For each passage, jot the author\'s main claim in 5 words.',
      'Note where they agree and where they disagree.',
      'Answer based on that relationship — not on which author you prefer.',
    ],
    whenToUse: 'Paired-passage questions about agreement, disagreement, or shared ideas.',
    commonTrap: 'Attributing Passage A\'s idea to Passage B (or vice versa).',
  },
  'Tenses': {
    skill: 'Tenses',
    headline: 'Match tense to the timeline of the sentence',
    steps: [
      'Find the main verb and any time clues (yesterday, since, by 2020).',
      'Check nearby verbs — they usually need to stay consistent unless time shifts.',
      'Eliminate choices that jump tenses without a clear reason.',
    ],
    whenToUse: 'Verb tense / form questions in Standard English Conventions.',
    commonTrap: 'A grammatically correct verb that breaks the passage\'s established timeline.',
  },
  'Pronouns and Modifiers': {
    skill: 'Pronouns and Modifiers',
    headline: 'Make the reference and the modifier crystal clear',
    steps: [
      'For pronouns: circle the pronoun and draw an arrow to its noun — if unclear, it\'s wrong.',
      'For modifiers: the descriptive phrase must sit next to what it describes.',
      'Eliminate ambiguous "it/they/this" and dangling modifiers.',
    ],
    whenToUse: 'Pronoun agreement/reference or misplaced modifier questions.',
    commonTrap: 'A pronoun that could refer to two different nouns.',
  },
  'Referencing Data': {
    skill: 'Referencing Data',
    headline: 'Read the graph/table before the choices',
    steps: [
      'Read the title, axes/labels, and units first.',
      'Find the exact data point the question asks about.',
      'Match the claim to the numbers — don\'t estimate from the picture alone.',
    ],
    whenToUse: 'Questions that cite a table, chart, or figure in Reading & Writing.',
    commonTrap: 'Mixing up rows/columns or comparing the wrong categories.',
  },

  // --- Top-frequency Math ---
  'Linear Word Problems': {
    skill: 'Linear Word Problems',
    headline: 'Translate the story into an equation',
    steps: [
      'Define a variable for the unknown the question asks for.',
      'Translate key phrases: "is" → $=$, "of" → multiply, "per" → divide.',
      'Solve, then check that your answer matches the units in the question.',
    ],
    whenToUse: 'Story problems that lead to a linear equation or inequality.',
    commonTrap: 'Solving for the wrong quantity (e.g., finding $x$ when the question asks for $2x$).',
  },
  'Quadratic Equations': {
    skill: 'Quadratic Equations',
    headline: 'Choose the fastest path: factor, formula, or graph',
    steps: [
      'Write in standard form: $ax^2 + bx + c = 0$.',
      'Try factoring first; if messy, use the quadratic formula.',
      'Check which root(s) the question actually wants (and discard extras).',
    ],
    whenToUse: 'Solve, find roots, or interpret a quadratic equation/function.',
    commonTrap: 'Forgetting the $\\pm$ or keeping an extraneous root.',
  },
  'Linear Equations': {
    skill: 'Linear Equations',
    headline: 'Isolate the variable, one clean step at a time',
    steps: [
      'Simplify both sides (distribute, combine like terms).',
      'Move variable terms to one side and constants to the other.',
      'Divide/multiply to isolate $x$, then plug back in to verify.',
    ],
    whenToUse: 'One-variable linear equations, including with fractions or decimals.',
    commonTrap: 'Sign errors when moving terms across the equals sign.',
  },
  'Geometry': {
    skill: 'Geometry',
    headline: 'Draw it, label it, then apply one formula',
    steps: [
      'Sketch the figure and mark known lengths/angles.',
      'Identify the shape and the formula you need (area, perimeter, volume, angle).',
      'Substitute carefully and check units.',
    ],
    whenToUse: 'Area, perimeter, volume, or angle questions.',
    commonTrap: 'Using diameter when the formula needs radius (or vice versa).',
  },
  'Algebra': {
    skill: 'Algebra',
    headline: 'Simplify expressions before you solve',
    steps: [
      'Expand or factor to reveal like terms.',
      'Combine like terms; keep track of signs.',
      'Isolate the target variable or expression the question asks for.',
    ],
    whenToUse: 'Manipulate expressions, solve for a variable in terms of others.',
    commonTrap: 'Distributing a negative incorrectly: $-(x-3) \\neq -x - 3$.',
  },
  'Systems of Equations': {
    skill: 'Systems of Equations',
    headline: 'Elimination or substitution — pick one and commit',
    steps: [
      'Decide: substitution (easy isolated variable) or elimination (matching coefficients).',
      'Solve for one variable, then plug back to find the other.',
      'Check both equations with your pair $(x, y)$.',
    ],
    whenToUse: 'Two equations, two unknowns.',
    commonTrap: 'Finding $x$ but forgetting to solve for $y$ (or reporting the wrong one).',
  },
  'Interpreting Graphs': {
    skill: 'Interpreting Graphs',
    headline: 'Read axes first, then the story of the line/curve',
    steps: [
      'Note what each axis measures and the scale.',
      'Identify slope (rate of change) and intercepts (starting values).',
      'Match the graph feature to the question\'s wording.',
    ],
    whenToUse: 'Questions about graphs of linear/quadratic/exponential relationships.',
    commonTrap: 'Confusing slope with $y$-intercept, or reading the wrong axis.',
  },
  'Percent': {
    skill: 'Percent',
    headline: 'Translate % into multiply-by-decimal',
    steps: [
      'Convert: $p\\%$ of $n$ → $0.0p \\times n$ (or $\\frac{p}{100} \\times n$).',
      'For percent change: $\\frac{\\text{new} - \\text{old}}{\\text{old}} \\times 100\\%$.',
      'Watch for "of what" vs "what percent of" — set up the equation carefully.',
    ],
    whenToUse: 'Percent of, percent increase/decrease, or reverse-percent problems.',
    commonTrap: 'Using the new value as the denominator for percent change.',
  },
  'Statistics': {
    skill: 'Statistics',
    headline: 'Know which measure answers the question',
    steps: [
      'Mean = average; median = middle; mode = most frequent; range = max − min.',
      'Organize the data (sort if needed) before calculating.',
      'For graphs, read frequencies carefully — don\'t count categories as values.',
    ],
    whenToUse: 'Mean, median, mode, range, or data-set comparison questions.',
    commonTrap: 'Forgetting to order the list before finding the median.',
  },
  'Probability': {
    skill: 'Probability',
    headline: 'Favorable over total — count carefully',
    steps: [
      'Define the sample space (all equally likely outcomes).',
      'Count favorable outcomes that match the event.',
      'Simplify the fraction; for "and/or" events, decide if outcomes overlap.',
    ],
    whenToUse: 'Simple probability, combinations of events, or reading probability from tables.',
    commonTrap: 'Double-counting outcomes or forgetting that probabilities must sum to 1.',
  },
  'Circles': {
    skill: 'Circles',
    headline: 'Radius is the key — everything else follows',
    steps: [
      'Write down $r$ (half of diameter if needed).',
      'Use $C = 2\\pi r$, $A = \\pi r^2$, or arc/sector formulas as needed.',
      'Leave answers in terms of $\\pi$ unless the question asks for a decimal.',
    ],
    whenToUse: 'Circumference, area, arcs, sectors, or circle equation questions.',
    commonTrap: 'Using diameter in a formula that requires radius.',
  },
  'Trigonometry': {
    skill: 'Trigonometry',
    headline: 'SOH-CAH-TOA on a labeled right triangle',
    steps: [
      'Sketch and label opposite, adjacent, and hypotenuse relative to the angle.',
      'Pick the ratio that uses the sides you know and the side you need.',
      'Solve for the unknown; check that the angle matches the sides you labeled.',
    ],
    whenToUse: 'Right-triangle trig or trig ratios in the coordinate plane.',
    commonTrap: 'Mixing up opposite and adjacent for the given angle.',
  },
  'Exponential Equations': {
    skill: 'Exponential Equations',
    headline: 'Same base → equate exponents; otherwise take logs or rewrite',
    steps: [
      'Rewrite both sides with the same base when possible.',
      'If $a^x = a^y$, then $x = y$.',
      'Check growth/decay context: is the base $>1$ (growth) or $0 < b < 1$ (decay)?',
    ],
    whenToUse: 'Solve exponential equations or interpret exponential models.',
    commonTrap: 'Treating $2^{x+1}$ as $2^x + 1$.',
  },
  'Exponential Word Problems': {
    skill: 'Exponential Word Problems',
    headline: 'Identify start value, rate, and time',
    steps: [
      'Write $y = a(1 \\pm r)^t$ or $y = a \\cdot b^t$ from the story.',
      'Plug in the given time or target value.',
      'Interpret whether the answer is a population, dollar amount, etc.',
    ],
    whenToUse: 'Growth/decay word problems (interest, populations, half-life style).',
    commonTrap: 'Using the percent as $r$ without converting to a decimal.',
  },
  'Inequality Word Problems': {
    skill: 'Inequality Word Problems',
    headline: 'Translate, solve, then check the inequality direction',
    steps: [
      'Translate words: "at least" ≥, "at most" ≤, "more than" >.',
      'Solve like an equation, flipping the inequality if you multiply/divide by a negative.',
      'Test one number from your solution set in the original story.',
    ],
    whenToUse: 'Word problems that ask for a range or minimum/maximum constraint.',
    commonTrap: 'Forgetting to flip the inequality when dividing by a negative.',
  },
  'Polynomial Expressions': {
    skill: 'Polynomial Expressions',
    headline: 'Factor or expand — then simplify',
    steps: [
      'Look for a GCF first, then difference of squares or trinomials.',
      'When expanding, distribute every term (FOIL / box method).',
      'Combine like terms and match the form the question asks for.',
    ],
    whenToUse: 'Factor, expand, or rewrite polynomial expressions.',
    commonTrap: 'Dropping the middle term when squaring a binomial: $(x+3)^2 \\neq x^2 + 9$.',
  },
  'Absolute Value': {
    skill: 'Absolute Value',
    headline: 'Absolute value means distance from zero',
    steps: [
      'Rewrite $|A| = B$ as $A = B$ or $A = -B$ (if $B \\ge 0$).',
      'Solve each case separately.',
      'Check solutions in the original equation — discard extras.',
    ],
    whenToUse: 'Equations or inequalities with absolute value.',
    commonTrap: 'Solving $|A| = B$ when $B$ is negative (no solution).',
  },
  'Unit Conversions': {
    skill: 'Unit Conversions',
    headline: 'Multiply by fractions that equal 1',
    steps: [
      'Write the starting quantity with its unit.',
      'Multiply by conversion fractions so unwanted units cancel.',
      'Confirm the final unit matches what the question asks for.',
    ],
    whenToUse: 'Any problem that switches units (time, distance, rates, currency).',
    commonTrap: 'Flipping the conversion fraction so units don\'t cancel.',
  },
};

const CATEGORY_FALLBACK: Record<string, StrategyCardData> = {
  'Reading and Writing': {
    skill: 'Reading and Writing',
    headline: 'Use the text as your only evidence',
    steps: [
      'Read the question stem carefully — know what it asks before looking at choices.',
      'Go back to the relevant lines in the passage.',
      'Eliminate answers that add outside info or contradict the text.',
    ],
    whenToUse: 'Any Reading & Writing question when a skill-specific card isn\'t available.',
  },
  'Math': {
    skill: 'Math',
    headline: 'Define, set up, solve, check',
    steps: [
      'Identify what the question is asking for (and in what units).',
      'Write an equation or expression from the given info.',
      'Solve carefully, then plug your answer back in to verify.',
    ],
    whenToUse: 'Any Math question when a skill-specific card isn\'t available.',
  },
};

const GENERIC_FALLBACK: StrategyCardData = {
  skill: 'General',
  headline: 'Slow down and use a clear process',
  steps: [
    'Restate the question in your own words.',
    'Eliminate obviously wrong choices first.',
    'Check your answer against the question\'s ask — not just your calculation.',
  ],
  whenToUse: 'Any SAT question when you feel stuck.',
};

function skillCategory(skill: string): string | null {
  for (const [category, skills] of Object.entries(categories)) {
    if ((skills as readonly string[]).includes(skill)) return category;
  }
  return null;
}

export function getStrategyCard(skill: string): StrategyCardData {
  if (cards[skill]) return cards[skill];
  const category = skillCategory(skill);
  if (category && CATEGORY_FALLBACK[category]) {
    return { ...CATEGORY_FALLBACK[category], skill };
  }
  return { ...GENERIC_FALLBACK, skill: skill || 'General' };
}

/** All skill names that have a dedicated card (for content audits). */
export function getCoveredSkills(): string[] {
  return Object.keys(cards);
}

export type { SkillName };
